import React from "react";
import { Avatar, Box } from "@mui/material";
import useAgentDisplayProfile from "../../hooks/useAgentDisplayProfile";
import useUIPreferenceStore from "../../stores/useUIPreferenceStore";
import { stringAvatar, stringToColor } from "../../utils/AvatarUtils";
import {
  AGENT_TIER,
  SYSTEM_SENDER_NAME,
  TIER_BORDER_COLOR,
} from "../../utils/agentDisplayUtils";
import { PixelPerson } from "../pixelPerson";
import {
  MASTER_AGENT_HEADWEAR_ID,
  MASTER_AGENT_PIXEL_COLORS,
  SYSTEM_AGENT_HEADWEAR_ID,
  SYSTEM_AGENT_PIXEL_COLORS,
  sanitizeCssColor,
} from "../pixelPerson/pixelPersonUtils";

/**
 * Sprite chrome per tier. `palette === null` means a name-seeded sprite; `headwear`
 * is the headwear id passed to PixelPerson. Everything an avatar renders is derived
 * from the resolved tier, so there is exactly one place that encodes tier precedence.
 */
const TIER_SPRITE = {
  [AGENT_TIER.MASTER]: { palette: MASTER_AGENT_PIXEL_COLORS, headwear: MASTER_AGENT_HEADWEAR_ID },
  [AGENT_TIER.SYSTEM_FIXED]: { palette: SYSTEM_AGENT_PIXEL_COLORS, headwear: SYSTEM_AGENT_HEADWEAR_ID },
  [AGENT_TIER.SYSTEM_SCOPED]: { palette: null, headwear: SYSTEM_AGENT_HEADWEAR_ID },
  [AGENT_TIER.CUSTOMER]: { palette: null, headwear: undefined },
};

const SYSTEM_TIERS = new Set([AGENT_TIER.SYSTEM_FIXED, AGENT_TIER.SYSTEM_SCOPED]);

/** Letter-avatar chip styles must not bleed into the pixel-person wrapper. */
function resolveSpriteWrapperSx(sx) {
  if (!sx) {
    return {};
  }
  const { bgcolor, color, ...spriteSx } = sx;
  return spriteSx;
}

/**
 * Centralised avatar for agents, users, and channels.
 *
 * Pass `agent`, `agentId`, or `agentName` to resolve avatar URL and tier chrome
 * from the agents store. Context/system agents get the white hardhat; Master Agent
 * gets the black helmet; customer agents use name-seeded sprites.
 * Pass only `name` for humans and other non-agent identities.
 */
export default function AgentAvatar({
  name,
  src,
  agent,
  agentId,
  agentName,
  forceAvatar = false,
  forceSprite = false,
  size = 28,
  borderRadius,
  fontSize,
  onClick,
  sx,
  children,
  ...rest
}) {
  const lookupAgentName = agentName ?? (agent || agentId ? name : undefined);
  const shouldResolve = Boolean(agent || agentId || lookupAgentName);
  const profile = useAgentDisplayProfile({
    agent,
    agentId,
    agentName: lookupAgentName,
    enabled: shouldResolve,
  });

  const isSystemIdentity = name === SYSTEM_SENDER_NAME;
  const resolvedName = name ?? profile.displayName ?? "?";
  const resolvedSrc = src ?? profile.avatarUrl ?? undefined;
  const tier = isSystemIdentity
    ? AGENT_TIER.SYSTEM_FIXED
    : shouldResolve
      ? profile.tier
      : AGENT_TIER.CUSTOMER;

  const agentDisplayStyle = useUIPreferenceStore((s) => s.agentDisplayStyle);

  if (!forceAvatar && (forceSprite || agentDisplayStyle === "sprite")) {
    const { palette, headwear } = TIER_SPRITE[tier];
    const spriteColors = palette ?? {
      shirt: sanitizeCssColor(stringToColor(resolvedName), "#2196F3"),
    };

    return (
      <Box
        sx={{
          lineHeight: 0,
          flexShrink: 0,
          display: "inline-flex",
          ...resolveSpriteWrapperSx(sx),
        }}
      >
        <PixelPerson
          name={resolvedName}
          size={size}
          {...spriteColors}
          hairStyle={spriteColors.hairStyle}
          headwear={headwear}
          masterAgent={tier === AGENT_TIER.MASTER}
          systemAgent={SYSTEM_TIERS.has(tier)}
          onClick={onClick}
          {...rest}
        />
      </Box>
    );
  }

  const avatarProps = stringAvatar(resolvedName);
  const hasImage = Boolean(resolvedSrc);

  const resolvedRadius = borderRadius ?? (size <= 24 ? `${Math.round(size * 0.2)}px` : "6px");
  const resolvedFontSize = fontSize ?? (size <= 20 ? "10px" : size <= 28 ? "12px" : size <= 40 ? "14px" : "20px");

  const clickSx = onClick
    ? {
        cursor: "pointer",
        transition: "transform 140ms ease, box-shadow 140ms ease, filter 140ms ease",
        "&:hover": {
          transform: "translateY(-1px)",
          boxShadow: "0 2px 8px rgba(24, 24, 27, 0.2)",
          filter: "brightness(0.98)",
        },
        "&:active": {
          transform: "translateY(0)",
          boxShadow: "0 1px 4px rgba(24, 24, 27, 0.16)",
        },
      }
    : {};

  const tierBorderColor = TIER_BORDER_COLOR[tier];
  const tierBorderSx = tierBorderColor ? { border: `2px solid ${tierBorderColor}` } : {};

  return (
    <Avatar
      variant="rounded"
      src={hasImage ? resolvedSrc : undefined}
      {...(hasImage ? {} : avatarProps)}
      {...rest}
      {...(!hasImage && children != null ? { children } : {})}
      onClick={onClick}
      sx={{
        ...(hasImage ? {} : avatarProps.sx),
        width: size,
        height: size,
        borderRadius: resolvedRadius,
        fontSize: resolvedFontSize,
        fontWeight: 600,
        flexShrink: 0,
        ...(hasImage ? { bgcolor: "background.paper" } : {}),
        ...clickSx,
        ...tierBorderSx,
        ...sx,
      }}
    />
  );
}
